/**
 * Games API - PocketBase version
 * Replaces src/api/games.js for PocketBase backend
 */

import pb, { customApi, getFileUrl } from '../pocketbase';

// ============ COVER URLs ============

// Публичная обложка (thumb)
export function getCoverUrl(record) {
    // Use cover_thumb field if available, otherwise fall back to cover
    if (record?.cover_thumb) {
        return getFileUrl(record, record.cover_thumb);
    }
    if (!record?.cover) return null;
    return getFileUrl(record, record.cover);
}

// Полноразмерная обложка
export function getFullCoverUrl(record) {
    if (!record?.cover) return null;
    return getFileUrl(record, record.cover);
}

// Helper: enriches game object with cover URLs for backward compatibility
function enrichGameWithUrls(game) {
    if (!game) return null;
    return {
        ...game,
        coverUrl: getCoverUrl(game),
        fullCoverUrl: getFullCoverUrl(game),
        // Backward compatibility
        cover_path: game.cover,
        price_cents: game.price * 100, // PB stores price in rubles
    };
}

// ============ LIBRARY (MarketPage) ============

// Список игр для MarketPage
export async function fetchLibraryGames({
    page = 1,
    pageSize = 24,
    sort = 'created_desc',
    q = ''
} = {}) {
    try {
        // Build filter
        let filter = "status = 'published'";
        if (q && q.trim()) {
            filter += ` && (title ~ '${q.trim()}' || subtitle ~ '${q.trim()}')`;
        }

        // Build sort
        let sortField = '-created';
        switch (sort) {
            case 'title_asc':
                sortField = 'title';
                break;
            case 'popular':
            case 'created_desc':
            default:
                sortField = '-created';
                break;
        }

        const result = await pb.collection('games').getList(page, pageSize, {
            filter,
            sort: sortField,
        });

        return {
            items: result.items.map(enrichGameWithUrls),
            total: result.totalItems,
            page: result.page,
            pageSize: result.perPage
        };
    } catch (error) {
        console.error('Error fetching library games:', error);
        throw error;
    }
}

// Получить публичную информацию об игре по slug
export async function fetchPublicGame(slug) {
    try {
        const record = await pb.collection('games').getFirstListItem(
            `slug = '${slug}' && status = 'published'`
        );
        return record;
    } catch (error) {
        if (error.status === 404) {
            return null;
        }
        throw error;
    }
}

// ============ MY GAMES ============

// Список игр пользователя
export async function fetchMyGamesWithPagination({
    page = 1,
    pageSize = 24,
    sort = 'created_desc',
    q = ''
} = {}) {
    try {
        if (!pb.authStore.isValid) {
            return { items: [], total: 0, page, pageSize };
        }

        const userId = pb.authStore.model.id;

        // Get user's purchases
        let filter = `user = '${userId}' && status = 'paid'`;

        let sortField = '-created';
        switch (sort) {
            case 'title_asc':
                sortField = 'game.title';
                break;
            case 'access_granted_desc':
                sortField = '-created';
                break;
            default:
                sortField = '-created';
        }

        const purchases = await pb.collection('purchases').getList(page, pageSize, {
            filter,
            sort: sortField,
            expand: 'game',
        });

        // Transform to game objects with cover URLs
        const items = purchases.items.map(purchase => {
            const game = purchase.expand?.game;
            if (!game) return null;
            return {
                ...enrichGameWithUrls(game),
                access_type: purchase.type, // 'full' or 'trial'
                access_via: purchase.type === 'full' ? 'purchase' : 'trial',
                access_granted_at: purchase.created,
            };
        }).filter(Boolean);

        // Apply search filter on client side (for expanded games)
        let filteredItems = items;
        if (q && q.trim()) {
            const searchLower = q.trim().toLowerCase();
            filteredItems = items.filter(game =>
                game.title?.toLowerCase().includes(searchLower) ||
                game.subtitle?.toLowerCase().includes(searchLower)
            );
        }

        return {
            items: filteredItems,
            total: purchases.totalItems,
            page: purchases.page,
            pageSize: purchases.perPage
        };
    } catch (error) {
        console.error('Error fetching my games:', error);
        throw error;
    }
}

// Legacy function
export async function fetchMyGames() {
    const result = await fetchMyGamesWithPagination({ pageSize: 100 });
    return result.items;
}

// ============ GAME ACCESS ============

// Статус доступа к игре
export async function getGameAccess(gameId) {
    try {
        const data = await customApi('GET', `/api/custom/game-access/${gameId}`);
        return data;
    } catch (error) {
        console.error('Error getting game access:', error);
        return { access: 'none' };
    }
}

// Бесплатное получение по slug
export async function claimFreeGameBySlug(slug) {
    try {
        const data = await customApi('POST', `/api/custom/claim-free/${slug}`);
        return data.success;
    } catch (error) {
        console.error('Error claiming free game:', error);
        throw error;
    }
}

// Пробная версия по slug
export async function claimTrialBySlug(slug) {
    try {
        const data = await customApi('POST', `/api/custom/claim-trial/${slug}`);
        return data.success;
    } catch (error) {
        console.error('Error claiming trial:', error);
        throw error;
    }
}

// Проверка доступа к игре
export async function checkGameAccess(gameId) {
    if (!pb.authStore.isValid) return false;

    try {
        const access = await getGameAccess(gameId);
        return access.access === 'full' || access.access === 'trial';
    } catch {
        return false;
    }
}

// ============ GAME CARDS ============

// Загрузка карточек игры с учетом доступа
export async function fetchGameCards(gameId) {
    if (!gameId) return [];

    try {
        const data = await customApi('GET', `/api/custom/game-cards/${gameId}`);

        // Get game record for file URLs
        const game = await pb.collection('games').getOne(gameId);

        return data.cards.map((card, index) => {
            // Get card record for file URL
            const cardNumber = String(index + 1).padStart(2, '0');

            // Build URLs - assuming cards have image file field
            const baseUrl = pb.baseURL;
            const cardPicUrl = card.image
                ? `${baseUrl}/api/files/cards/${card.id}/${card.image}`
                : `${baseUrl}/api/files/games/${gameId}/webp/${cardNumber}.webp`;

            // Use image_thumb field if available, otherwise fall back to image
            const cardThumbUrl = card.image_thumb
                ? `${baseUrl}/api/files/cards/${card.id}/${card.image_thumb}`
                : (card.image
                    ? `${baseUrl}/api/files/cards/${card.id}/${card.image}`
                    : `${baseUrl}/api/files/games/${gameId}/webp-thumbs/${cardNumber}.webp`);

            return {
                cardPicUrl,
                cardThumbUrl,
                question1: card.question,
                question2: card.subquestion || '',
                accessType: card.access_type,
                id: card.id
            };
        });
    } catch (error) {
        console.error('Error fetching game cards:', error);
        throw error;
    }
}

// ============ STORAGE HELPERS ============

// Universal helper for storage URLs
export function getPublicUrlByStoragePath(storagePath, defaultBucket = 'cards') {
    if (!storagePath || typeof storagePath !== 'string') return null;
    if (storagePath.startsWith('http://') || storagePath.startsWith('https://')) return storagePath;

    // For PocketBase, files are accessed via the files API
    // This will need adjustment based on how you store files
    return `${pb.baseURL}/api/files/${defaultBucket}/${storagePath}`;
}

export default {
    fetchLibraryGames,
    fetchPublicGame,
    fetchMyGamesWithPagination,
    fetchMyGames,
    getGameAccess,
    claimFreeGameBySlug,
    claimTrialBySlug,
    checkGameAccess,
    fetchGameCards,
    getCoverUrl,
    getFullCoverUrl,
    getPublicUrlByStoragePath,
};
